import React, { useState, useEffect } from 'react';
import { usersAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import './CustomerList.css';

function CustomerList() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    loadCustomers(currentPage);
  }, [currentPage]);

  const loadCustomers = async (page) => {
    setLoading(true);
    try {
      const response = await usersAPI.getAll('customer', page, 25);
      setCustomers(response.data.users);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error loading customers:', error);
      alert('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerClick = (customerId) => {
    navigate(`/admin/customers/${customerId}`);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo(0, 0);
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first, last, and pages around current
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return (
      <div className="pagination">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="pagination-btn"
        >
          Previous
        </button>

        {pages.map((page, index) => (
          page === '...' ? (
            <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
          ) : (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
            >
              {page}
            </button>
          )
        ))}

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="pagination-btn"
        >
          Next
        </button>
      </div>
    );
  };

  if (loading && currentPage === 1) {
    return <div className="loading">Loading customers...</div>;
  }

  return (
    <div className="page customer-list-page">
      <div className="page-header">
        <h1 className="page-title">Customer Management</h1>
        <p className="customer-count">{total} total customer{total !== 1 ? 's' : ''}</p>
      </div>

      {customers.length === 0 ? (
        <div className="card">
          <p>No customers found.</p>
        </div>
      ) : (
        <>
          <div className="customers-grid">
            {customers.map((customer) => (
              <div
                key={customer.id}
                className="customer-card"
                onClick={() => handleCustomerClick(customer.id)}
              >
                <div className="customer-header">
                  <div className="customer-avatar">
                    {customer.first_name[0]}{customer.last_name[0]}
                  </div>
                  <div className="customer-info">
                    <h3>{customer.first_name} {customer.last_name}</h3>
                    <p className="customer-email">{customer.email}</p>
                  </div>
                </div>

                <div className="customer-details">
                  {customer.phone && (
                    <div className="detail-item">
                      <span className="detail-label">Phone:</span>
                      <span className="detail-value">{customer.phone}</span>
                    </div>
                  )}
                  <div className="detail-item">
                    <span className="detail-label">Status:</span>
                    <span className={`status-badge ${customer.is_active ? 'active' : 'inactive'}`}>
                      {customer.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {customer.email_verified !== undefined && (
                    <div className="detail-item">
                      <span className="detail-label">Email:</span>
                      <span className={`verification-badge ${customer.email_verified ? 'verified' : 'unverified'}`}>
                        {customer.email_verified ? 'Verified' : 'Unverified'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="customer-footer">
                  <span className="view-profile">View Profile →</span>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination-container">
              {renderPagination()}
              <p className="pagination-info">
                Page {currentPage} of {totalPages}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default CustomerList;
